import { Navigate, useLocation } from 'react-router-dom'

import {
  useAccount,
  useChannelsConnected,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import { insightsChannel } from '~publish/legacy/routes'

import { AllChannelsInsightsEmptyState } from './AllChannelsInsights/AllChannelsInsightsEmptyState'
import { AllChannelsInsightsPage } from './AllChannelsInsights/AllChannelsInsightsPage'

import type { JSX } from 'react'

/**
 * Wrapper component that chooses which Insights view to render based on how
 * many channels the actor can access:
 * - none, and the actor can connect channels: the fresh-start
 *   AllChannelsInsightsEmptyState (connect-channel hero + blank teasers)
 * - none, and the actor can't connect channels (e.g. a non-admin team member):
 *   the same empty state with the connect-channel hero hidden — prompting them
 *   to connect a channel they can't connect would be misleading (CORE-6008).
 *   Mirrors how Publish hides its connect CTA from non-admins.
 * - one: redirect to that channel's single-channel insights
 * - many: the AllChannelsInsightsPage
 */
export const InsightsPageOrSingleChannelRedirect = (): JSX.Element => {
  const location = useLocation()
  const { loading } = useAccount()
  const connectedChannels = useChannelsConnected()
  // Whether to surface the connect-channel hero. Publish gates its connect CTA
  // on owner/admin; `canManageChannels` is the underlying capability, so use it.
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const hasNoChannels = connectedChannels?.length === 0
  const hasSingleChannel = connectedChannels?.length === 1

  // `useChannelsConnected` returns [] while the account is still loading, so
  // gate on `loading` — otherwise users with channels flash the empty state
  // before the org data arrives and we redirect/render the real page.
  if (!loading && hasNoChannels) {
    // Hide the connect-channel hero from actors who can't manage channels —
    // prompting them to connect one would be misleading (CORE-6008). The page
    // chrome and blank teaser sections still render.
    return <AllChannelsInsightsEmptyState hideConnectCta={!canManageChannels} />
  }

  if (hasSingleChannel) {
    const channelId = connectedChannels[0]?.id
    if (channelId) {
      return (
        <Navigate
          to={{
            pathname: insightsChannel.buildPathname({ channelId }),
            search: location.search,
            hash: location.hash,
          }}
          replace
        />
      )
    }
  }

  return <AllChannelsInsightsPage />
}
